/**
 * TreeNode represents a single node in the JSON tree structure
 */
export class TreeNode {
  /**
   * @param {string} key - The key/property name for this node
   * @param {any} value - The actual value stored in this node
   * @param {string} type - The type of the value ('object', 'array', 'string', 'number', 'boolean', 'null')
   * @param {string[]} path - Array representing the path to this node
   * @param {number} level - The depth level of this node in the tree
   */
  constructor(key, value, type, path = [], level = 0) {
    this.key = key;
    this.value = value;
    this.type = type;
    this.path = [...path];
    this.level = level;
    this.children = [];
    this.isExpanded = level < 2; // Auto-expand first 2 levels by default
  }

  /**
   * Add a child node to this node
   * @param {TreeNode} childNode - The child node to add
   */
  addChild(childNode) {
    this.children.push(childNode);
  }

  /**
   * Check if this node has children
   * @returns {boolean}
   */
  hasChildren() {
    return this.children.length > 0;
  }

  /**
   * Get the full path as a string
   * @returns {string}
   */
  getPathString() {
    return this.path.join('.');
  }

  /**
   * Check if this node is a container type (object or array)
   * @returns {boolean}
   */
  isContainer() {
    return this.type === 'object' || this.type === 'array';
  }

  /**
   * Get a display-friendly representation of the value
   * @returns {string}
   */
  getDisplayValue() {
    if (this.type === 'string') {
      return `"${this.value}"`;
    }
    if (this.type === 'null') {
      return 'null';
    }
    if (this.type === 'object') {
      return `{${Object.keys(this.value).length} properties}`;
    }
    if (this.type === 'array') {
      return `[${this.value.length} items]`;
    }
    return String(this.value);
  }

  /**
   * Toggle the expanded state of this node
   */
  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Expand this node and optionally all children
   * @param {boolean} recursive - Whether to expand all children recursively
   */
  expand(recursive = false) {
    this.isExpanded = true;
    if (recursive) {
      this.children.forEach(child => child.expand(true));
    }
  }

  /**
   * Collapse this node and optionally all children
   * @param {boolean} recursive - Whether to collapse all children recursively
   */
  collapse(recursive = false) {
    this.isExpanded = false;
    if (recursive) {
      this.children.forEach(child => child.collapse(true));
    }
  }
}